import { useMemo } from 'react'
import type { MetricSnapshot } from '../../types'

interface HealthStatusTrackerProps {
  timeSeries: MetricSnapshot[]
  loading?: boolean
}

type HealthStatus = 'healthy' | 'degraded' | 'unhealthy' | 'unknown'

function getHealthStatus(snapshot: MetricSnapshot): HealthStatus {
  const { metrics } = snapshot

  // Check error rate
  const requests = metrics['http_requests_total'] ?? 0
  const errors = metrics['http_errors_total'] ?? 0
  const errorRate = requests > 0 ? (errors / requests) * 100 : 0

  // Check response time (p95)
  const p95 = metrics['http_request_duration_p95'] ?? null

  // Check CPU/Memory
  const cpu = metrics['cpu_usage_percent'] ?? null
  const memory = metrics['memory_usage_percent'] ?? null

  // Determine health based on thresholds
  if (errorRate > 10 || (p95 !== null && p95 > 5000) || (cpu !== null && cpu > 90)) {
    return 'unhealthy'
  }
  if (
    errorRate > 2 ||
    (p95 !== null && p95 > 2000) ||
    (cpu !== null && cpu > 75) ||
    (memory !== null && memory > 85)
  ) {
    return 'degraded'
  }
  if (requests > 0 || cpu !== null || memory !== null) {
    return 'healthy'
  }
  return 'unknown'
}

const statusColors = {
  healthy: 'bg-green-500',
  degraded: 'bg-amber-500',
  unhealthy: 'bg-red-500',
  unknown: 'bg-slate-300',
}

const statusLabels = {
  healthy: 'Healthy',
  degraded: 'Degraded',
  unhealthy: 'Unhealthy',
  unknown: 'No Data',
}

export function HealthStatusTracker({ timeSeries, loading = false }: HealthStatusTrackerProps) {
  // Get last 24 data points (or fewer)
  const healthBlocks = useMemo(() => {
    const recent = timeSeries.slice(-24)
    return recent.map(snapshot => ({
      timestamp: snapshot.created_at,
      status: getHealthStatus(snapshot),
    }))
  }, [timeSeries])

  // Count statuses
  const statusCounts = useMemo(() => {
    const counts: Record<HealthStatus, number> = {
      healthy: 0,
      degraded: 0,
      unhealthy: 0,
      unknown: 0,
    }
    healthBlocks.forEach(block => {
      counts[block.status]++
    })
    return counts
  }, [healthBlocks])

  // Calculate uptime percentage (healthy / total non-unknown)
  const uptimePercent = useMemo(() => {
    const knownBlocks = statusCounts.healthy + statusCounts.degraded + statusCounts.unhealthy
    if (knownBlocks === 0) return null
    return ((statusCounts.healthy + statusCounts.degraded * 0.5) / knownBlocks) * 100
  }, [statusCounts])

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-slate-900">Health Status</h3>
        {uptimePercent !== null && (
          <span
            className={`text-sm font-medium ${
              uptimePercent >= 99
                ? 'text-green-600'
                : uptimePercent >= 95
                  ? 'text-amber-600'
                  : 'text-red-600'
            }`}
          >
            {uptimePercent.toFixed(1)}% uptime
          </span>
        )}
      </div>

      {loading ? (
        <div className="h-12 flex items-center justify-center">
          <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-primary-500" />
        </div>
      ) : healthBlocks.length === 0 ? (
        <div className="h-12 flex items-center justify-center text-slate-400 text-sm">
          No health data available
        </div>
      ) : (
        <>
          {/* Status blocks */}
          <div className="flex gap-0.5 h-8 rounded overflow-hidden">
            {healthBlocks.map((block, i) => (
              <div
                key={i}
                className={`flex-1 ${statusColors[block.status]} hover:opacity-80 transition-opacity cursor-default`}
                title={`${new Date(block.timestamp).toLocaleString()}: ${statusLabels[block.status]}`}
              />
            ))}
          </div>

          {/* Legend */}
          <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
            <div className="flex items-center gap-3">
              {(Object.keys(statusColors) as HealthStatus[]).map(
                status =>
                  statusCounts[status] > 0 && (
                    <div key={status} className="flex items-center gap-1">
                      <div className={`w-2 h-2 rounded-full ${statusColors[status]}`} />
                      <span>
                        {statusLabels[status]} ({statusCounts[status]})
                      </span>
                    </div>
                  )
              )}
            </div>
            <span className="text-slate-400">Last {healthBlocks.length} samples</span>
          </div>
        </>
      )}
    </div>
  )
}
